// LandingPage.jsx - Conversion focused, no free trial mentions
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Brain, 
  Zap, 
  Eye, 
  TrendingUp, 
  Star, 
  ArrowRight, 
  Sparkles, 
  Target, 
  Search,
  Globe,
  CheckCircle,
  Users,
  Clock,
  BarChart3,
  Shield,
  Code2,
  Smartphone,
  Layers,
  ChevronRight,
  Lock,
  CreditCard
} from 'lucide-react';

export default function LandingPage() {
  const navigate = useNavigate();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [blinkingEye, setBlinkingEye] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setBlinkingEye(prev => !prev);
    }, 1200);
    return () => clearInterval(interval);
  }, []);

  // Mouse tracking for interactive effects
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Testimonial rotation
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleTryDemo = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?demo=${encodeURIComponent(searchQuery)}`);
    } else {
      navigate('/search');
    }
  };

  const features = [
    {
      icon: Brain,
      title: "Multi-Agent AI",
      description: "5 specialized AI agents work together like a world-class consulting team",
      gradient: "from-purple-400 to-pink-500"
    },
    {
      icon: Eye,
      title: "Visual Intelligence",
      description: "Understands design psychology and user experience patterns",
      gradient: "from-blue-400 to-cyan-500"
    },
    {
      icon: Zap,
      title: "Lightning Fast",
      description: "Get comprehensive analysis in under 3 seconds",
      gradient: "from-yellow-400 to-orange-500"
    },
    {
      icon: TrendingUp,
      title: "ROI Predictions",
      description: "See exactly how improvements will impact your revenue",
      gradient: "from-green-400 to-emerald-500"
    },
    {
      icon: Globe,
      title: "Proactive Discovery",
      description: "We find and analyze websites before you even know they need help",
      gradient: "from-indigo-400 to-purple-500"
    },
    {
      icon: Shield,
      title: "Enterprise Grade",
      description: "99.9% uptime with bank-level security and compliance",
      gradient: "from-red-400 to-pink-500"
    }
  ];

  const analysisCategories = [
    { icon: "🎨", label: "UX & Design", score: 92 },
    { icon: "⚡", label: "Performance", score: 88 },
    { icon: "🔍", label: "SEO", score: 95 },
    { icon: "📱", label: "Mobile", score: 90 },
    { icon: "♿", label: "Accessibility", score: 87 },
    { icon: "🔒", label: "Security", score: 93 }
  ];

  const testimonials = [
    {
      name: "Sarah Chen",
      role: "Lead Developer at TechCorp",
      content: "CodeRated saved us 40+ hours of manual analysis. The AI insights are incredibly accurate.",
      avatar: "👩‍💻",
      rating: 5
    },
    {
      name: "Marcus Rodriguez",
      role: "Freelance Web Designer",
      content: "My clients love the detailed reports. It's like having a senior developer review every project.",
      avatar: "👨‍🎨",
      rating: 5
    },
    {
      name: "Jennifer Park",
      role: "Digital Agency Owner",
      content: "We've increased our revenue by 60% using CodeRated's recommendations for client sites.",
      avatar: "👩‍💼",
      rating: 5
    }
  ];

  const pricingTiers = [
    {
      name: "Observer",
      price: "Free",
      period: "",
      description: "Get started with basic features",
      features: [
        "10 website analyses per month",
        "Basic scoring and insights",
        "Public feed access",
        "Community support"
      ],
      cta: "Start Free",
      ctaLink: "/register?tier=observer",
      popular: false,
      highlight: null
    },
    {
      name: "Professional",
      price: "$49",
      period: "/month",
      description: "Everything you need to excel",
      features: [
        "Unlimited website analyses",
        "Advanced AI insights & recommendations",
        "API access (1,000 calls/month)",
        "Export reports & data",
        "Email alerts for opportunities",
        "Priority support response"
      ],
      cta: "Get Professional",
      ctaLink: "/register?tier=professional",
      popular: true,
      highlight: "MOST POPULAR"
    },
    {
      name: "Business",
      price: "$149",
      period: "/month",
      description: "Scale your agency operations",
      features: [
        "Everything in Professional",
        "White-label reports with your branding",
        "Team collaboration (10 seats)",
        "Custom integrations",
        "Dedicated account manager",
        "API access (10,000 calls/month)"
      ],
      cta: "Get Business",
      ctaLink: "/register?tier=business",
      popular: false,
      highlight: "BEST VALUE"
    }
  ];

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Interactive background */}
      <div 
        className="fixed inset-0 opacity-30 pointer-events-none"
        style={{
          background: `radial-gradient(600px circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(99, 102, 241, 0.15), transparent 40%)`
        }}
      />
    
      <nav className="relative z-50 bg-slate-900/80 backdrop-blur-xl border-b border-slate-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* LEFT SIDE - LOGO */}
            <div className="flex items-center space-x-4">
              <Link to="/" className="group flex items-center gap-4 p-2 hover:scale-110 transition-all duration-300">
                <div className="relative">
                  <div className="absolute -inset-2 bg-gradient-to-r from-blue-500/30 to-cyan-500/30 rounded-xl blur-md opacity-0 group-hover:opacity-100 transition-all duration-300"></div>
                  <div className="relative w-12 h-10 bg-gradient-to-b from-gray-300 to-gray-500 rounded-lg border-2 border-gray-600 shadow-xl group-hover:shadow-2xl group-hover:shadow-blue-500/25 transition-all duration-300">
                    <div className="w-8 h-5 bg-black mx-auto mt-1 rounded border border-gray-800 flex items-center justify-center">
                      <div className={`transition-all duration-150 ${blinkingEye ? 'scale-100' : 'scale-y-10'}`}>
                        <Eye className="w-4 h-4 text-cyan-400 group-hover:text-cyan-300 transition-colors duration-300" />
                      </div>
                    </div>
                    <div className="flex justify-center mt-0.5 gap-1">
                      <div className="w-1 h-0.5 bg-red-500 rounded group-hover:bg-red-400 transition-colors duration-300"></div>
                      <div className="w-1 h-0.5 bg-green-500 rounded group-hover:bg-green-400 transition-colors duration-300"></div>
                    </div>
                  </div>
                  <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-4 h-2 bg-gray-600 rounded-b-lg group-hover:bg-gray-500 transition-colors duration-300"></div>
                </div>
                <span className="text-2xl font-bold text-white">CodeRated</span>
              </Link>
            </div>

            {/* CENTER NAV LINKS */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#home" className="text-slate-300 hover:text-white transition-colors">Home</a>
              <Link to="/feed" className="text-slate-300 hover:text-white transition-colors">Browse</Link>
              <a href="#pricing" className="text-slate-300 hover:text-white transition-colors">Pricing</a>
              <a href="#features" className="text-slate-300 hover:text-white transition-colors">Features</a>
              <a href="#how-it-works" className="text-slate-300 hover:text-white transition-colors">How it Works</a>
            </div>

            {/* RIGHT SIDE - AUTH LINKS */}
            <div className="flex items-center space-x-4">
              <Link to="/login" className="text-slate-300 hover:text-white transition-colors">
                Sign In
              </Link>
              <Link 
                to="/register" 
                className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-2 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 transition-all"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </nav>
    
      <div className="relative z-10">
        {/* Hero Section */}
        <section id="home" className="pt-20 pb-32 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-sm text-blue-200 mb-8 animate-bounce-subtle">
              <Sparkles className="w-4 h-4" />
              <span>Powered by Multi-Agent AI</span>
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            </div>

            <h1 className="text-6xl md:text-8xl font-black mb-8 relative">
              <span className="bg-gradient-to-r from-white via-blue-100 to-purple-200 bg-clip-text text-transparent drop-shadow-2xl">
                The Future of
                <br />
                Website Intelligence
              </span>
            </h1>

            <p className="text-2xl md:text-3xl text-slate-300 mb-6 font-light max-w-4xl mx-auto">
              Revolutionary AI that analyzes websites like a human expert. 
              Get <span className="text-blue-400 font-semibold">actionable insights</span> in seconds, not hours.
            </p>

            <p className="text-lg text-slate-400 max-w-3xl mx-auto mb-12 leading-relaxed">
              Our multi-agent AI system processes visual design, performance, SEO, and user experience 
              simultaneously, delivering insights that connect improvements directly to business results.
            </p>

            {/* Demo Search */}
            <div className="max-w-4xl mx-auto mb-12">
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-3xl blur-lg opacity-25 group-hover:opacity-40 transition duration-1000 group-hover:duration-200" />
                
                <form onSubmit={handleTryDemo} className="relative">
                  <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-700/50 rounded-3xl p-2 flex items-center gap-3">
                    <div className="flex-1 relative">
                      <div className="absolute left-6 top-1/2 transform -translate-y-1/2 text-slate-400">
                        <Brain className="w-5 h-5" />
                      </div>
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Try: apple.com, github.com, or any website..."
                        className="w-full pl-14 pr-6 py-6 bg-transparent text-white placeholder-slate-400 focus:outline-none text-lg font-medium"
                      />
                    </div>
                    
                    <button
                      type="submit"
                      className="group relative px-8 py-6 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white font-bold rounded-2xl hover:shadow-2xl hover:shadow-blue-500/25 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-900 transition-all duration-300 overflow-hidden"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <div className="relative flex items-center gap-3">
                        <Zap className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300" />
                        <span>Analyze Now</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                      </div>
                    </button>
                  </div>
                </form>
              </div>
              
              <div className="mt-6 text-center">
                <p className="text-slate-500 text-sm">
                  <Lock className="w-4 h-4 inline mr-1" />
                  Secure analysis • <span className="text-blue-400 font-semibold">95% accuracy</span>
                  <span className="mx-2">•</span>
                  Results in seconds
                </p>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-20 max-w-4xl mx-auto">
              <div className="group relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl blur opacity-0 group-hover:opacity-100 transition duration-500" />
                <div className="relative bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 text-center hover:bg-slate-900/80 transition-all duration-300 hover:transform hover:scale-105">
                  <div className="text-3xl font-black mb-2 bg-gradient-to-r from-blue-400 to-blue-300 bg-clip-text text-transparent">
                    100K+
                  </div>
                  <div className="text-slate-400 font-medium">Sites Analyzed</div>
                </div>
              </div>

              <div className="group relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-2xl blur opacity-0 group-hover:opacity-100 transition duration-500" />
                <div className="relative bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 text-center hover:bg-slate-900/80 transition-all duration-300 hover:transform hover:scale-105">
                  <div className="text-3xl font-black mb-2 bg-gradient-to-r from-purple-400 to-purple-300 bg-clip-text text-transparent">
                    2.1s
                  </div>
                  <div className="text-slate-400 font-medium">Avg Analysis</div>
                </div>
              </div>

              <div className="group relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-2xl blur opacity-0 group-hover:opacity-100 transition duration-500" />
                <div className="relative bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 text-center hover:bg-slate-900/80 transition-all duration-300 hover:transform hover:scale-105">
                  <div className="text-3xl font-black mb-2 bg-gradient-to-r from-green-400 to-green-300 bg-clip-text text-transparent">
                    95%
                  </div>
                  <div className="text-slate-400 font-medium">Accuracy</div>
                </div>
              </div>

              <div className="group relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-2xl blur opacity-0 group-hover:opacity-100 transition duration-500" />
                <div className="relative bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 text-center hover:bg-slate-900/80 transition-all duration-300 hover:transform hover:scale-105">
                  <div className="text-3xl font-black mb-2 bg-gradient-to-r from-yellow-400 to-orange-300 bg-clip-text text-transparent">
                    $2.4M
                  </div>
                  <div className="text-slate-400 font-medium">ROI Generated</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section - FIXED */}
        <section id="pricing" className="py-20 px-4 bg-slate-900/30">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-white mb-6">
                Simple, Transparent Pricing
              </h2>
              <p className="text-xl text-slate-400 max-w-3xl mx-auto">
                Choose the perfect plan for your needs. Upgrade anytime as you grow.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {pricingTiers.map((tier, index) => (
                <div key={index} className={`group relative ${tier.popular ? 'md:scale-105' : ''}`}>
                  {tier.highlight && (
                    <div className={`absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-1 rounded-full text-sm font-bold z-10 ${
                      tier.popular 
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                        : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white'
                    }`}>
                      {tier.highlight}
                    </div>
                  )}
                  
                  <div className={`absolute -inset-1 rounded-2xl blur opacity-0 group-hover:opacity-100 transition duration-500 ${
                    tier.popular 
                      ? 'bg-gradient-to-r from-blue-500/30 to-purple-500/30' 
                      : 'bg-gradient-to-r from-slate-700/30 to-slate-600/30'
                  }`} />
                  
                  <div className={`relative rounded-2xl p-8 h-full flex flex-col transition-all duration-300 ${
                    tier.popular
                      ? 'bg-slate-900/90 border-2 border-blue-500/50'
                      : 'bg-slate-900/60 border border-slate-700/50 hover:bg-slate-900/80'
                  }`}>
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-2">{tier.name}</h3>
                      <p className="text-slate-400 mb-6">{tier.description}</p>
                      
                      <div className="mb-6">
                        <span className="text-4xl font-black text-white">{tier.price}</span>
                        {tier.period && <span className="text-slate-400">{tier.period}</span>}
                      </div>
                    </div>
                    
                    <ul className="space-y-3 mb-8 flex-grow">
                      {tier.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start space-x-3">
                          <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-300 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <Link
                      to={tier.ctaLink}
                      className={`block w-full text-center py-3 rounded-lg font-semibold transition-all ${
                        tier.popular
                          ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700'
                          : tier.name === 'Observer'
                          ? 'bg-slate-800 text-white hover:bg-slate-700'
                          : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700'
                      }`}
                    >
                      {tier.cta}
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            {/* Enterprise Option */}
            <div className="mt-12 max-w-4xl mx-auto">
              <div className="bg-gradient-to-r from-purple-900/20 to-pink-900/20 backdrop-blur-xl border border-purple-500/30 rounded-2xl p-8">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">Enterprise</h3>
                    <p className="text-slate-400">Custom solutions for large organizations with specific needs</p>
                  </div>
                  <Link
                    to="/contact?plan=enterprise"
                    className="bg-gradient-to-r from-purple-500 to-pink-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-700 transition-all whitespace-nowrap"
                  >
                    Contact Sales
                  </Link>
                </div>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="mt-16 text-center">
              <div className="inline-flex items-center gap-8 text-slate-400">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  <span>Secure Payments</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  <span>SSL Encrypted</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  <span>Cancel Anytime</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-white mb-6">
                Revolutionary AI Architecture
              </h2>
              <p className="text-xl text-slate-400 max-w-3xl mx-auto">
                Five specialized AI agents work together like a world-class consulting team, 
                each focused on different aspects of website optimization.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <div key={index} className="group relative">
                    <div className="absolute -inset-1 bg-gradient-to-r from-slate-700/30 to-slate-600/30 rounded-2xl blur opacity-0 group-hover:opacity-100 transition duration-500" />
                    <div className="relative bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8 hover:bg-slate-900/80 transition-all duration-300 h-full">
                      <div className={`inline-flex items-center justify-center w-14 h-14 bg-gradient-to-r ${feature.gradient} rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300`}>
                        <IconComponent className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-slate-300 transition-all duration-300">
                        {feature.title}
                      </h3>
                      <p className="text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors duration-300">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Rest of sections remain the same... */}
        
        {/* CTA Section */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-3xl blur-xl opacity-30" />
              <div className="relative bg-slate-900/90 backdrop-blur-xl border border-slate-700/50 rounded-3xl p-12">
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                  Ready to Transform Your Website?
                </h2>
                <p className="text-xl text-slate-400 mb-8 max-w-2xl mx-auto">
                  Join thousands of developers and agencies using CodeRated to optimize 
                  their web presence and drive business results.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/register?tier=professional"
                    className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition-all flex items-center justify-center gap-2"
                  >
                    <span>Get Started Now</span>
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                  <Link
                    to="/search"
                    className="border border-slate-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-slate-800/50 transition-all flex items-center justify-center gap-2"
                  >
                    <span>Try Demo</span>
                    <Search className="w-5 h-5" />
                  </Link>
                </div>
                
                <p className="text-slate-500 text-sm mt-6">
                  Start with our free Observer plan • Upgrade anytime
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>       
    </div>
  );
}